<template>
    <div class="m-dialogs-page">
      <FeedHeader />
  
      <main class="m-dialogs-content">
        <div class="top-bar">
          <div class="menu-button" @click="toggleDropdown">
            <span class="menu-label">{{ selectedCategory }}</span>
            <img :src="dropdownIcon" class="dropdown-icon" />
            <span class="badge">
              {{ totalUnreadCount > 0 ? totalUnreadCount : "нет новых сообщений" }}
            </span>
          </div>
  
          <button class="add-button" @click="openCreateChatModal">
            <img src="/add-dialog.png" alt="+" />
          </button>
  
          <div v-if="dropdownVisible" class="dropdown-menu">
            <div
              v-for="option in ['Сообщения', 'Запросы']"
              :key="option"
              class="dropdown-item"
              :class="{ active: selectedCategory === option }"
              @click="selectCategory(option)"
            >
              {{ option }}
            </div>
          </div>
        </div>
  
        <div class="search-bar">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Поиск по сообщениям"
          />
        </div>
  
        <div class="dialogs-list">
          <template v-if="isLoadingDialogs">
            <div v-for="i in 6" :key="i" class="dialog-skeleton shimmer">
              <div class="avatar-skeleton"></div>
              <div class="text-group">
                <div class="text-line long"></div>
                <div class="text-line short"></div>
              </div>
            </div>
          </template>
  
          <template v-else-if="selectedCategory === 'Сообщения' && filteredDialogs.length > 0">
            <DialogItem
              v-for="dialog in filteredDialogs"
              :key="dialog.id"
              :dialog="dialog"
              :selected="false"
              @select="handleSelectDialog"
            />
          </template>
  
          <template v-else-if="selectedCategory === 'Запросы' && chatRequests.length > 0">
            <RequestItem
              v-for="request in chatRequests"
              :key="request.id"
              :request="request"
              :selected="false"
              @select="handleSelectRequest"
              @accept="handleRequestAccept"
              @decline="handleRequestDecline"
            />
          </template>
  
          <template v-else>
            <EmptyDialogsPlaceholder
              v-if="selectedCategory === 'Сообщения'"
              class="empty-state"
            />
            <EmptyRequestsDialogsPlaceholder
              v-else
              class="empty-state"
            />
          </template>
        </div>
      </main>
  
      <MobileTabBar />
  
      <FriendsDrawer
        v-model="isFriendsDrawerOpen"
        @select="handleOpenProfile"
      />
    </div>
  </template>
  
  <script setup>
  import { ref, computed, onMounted, watch, onBeforeUnmount } from "vue";
  import { useRouter, useRoute } from "vue-router";
  
  import FeedHeader from "../components/FeedHeader.vue";
  import MobileTabBar from "../components/MobileTabBar2.vue";
  
  import DialogItem from "../components/chats/DialogItem.vue";
  import RequestItem from "../components/chats/RequestItem.vue";
  import EmptyDialogsPlaceholder from "../components/chats/EmptyDialogsPlaceholder.vue";
  import EmptyRequestsDialogsPlaceholder from "../components/chats/EmptyRequestsDialogsPlaceholder.vue";
  import FriendsDrawer from "../components/FriendsDrawer.vue";
  
  import ChatsService from "../api/ChatsService.js";
  import RequestsService from "../api/RequestsService.js";
  import { authStateReady } from "../utils/authState";

  const router = useRouter();
  const route = useRoute();
  
  const dialogs = ref([]);
  const chatRequests = ref([]);
  
  const selectedCategory = ref("Сообщения");
  const searchQuery = ref("");
  const dropdownVisible = ref(false);
  
  const isLoadingDialogs = ref(true);
  const isFriendsDrawerOpen = ref(false);
  
  const totalUnreadCount = ref(0);
  const currentUserId = ref(null);
  
  const theme = ref(document.documentElement.getAttribute("data-theme") || "light");
  

  let unsubscribeAll = null;
  
  const filteredDialogs = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
  
    if (!query) {
      return dialogs.value;
    }
  
    return dialogs.value.filter(dialog => {
      const name = dialog.interlocutor?.name?.toLowerCase() || "";
      const specifics = dialog.interlocutor?.specifics?.toLowerCase() || "";
      const lastMessage = dialog.lastMessageText?.toLowerCase() || "";
  
      return (
        name.includes(query) ||
        specifics.includes(query) ||
        lastMessage.includes(query)
      );
    });
  });
  
  const dropdownIcon = computed(() => {
    return theme.value === "dark"
      ? "/down-arrow-dark.png"
      : "/down-arrow-light.png";
  });
  
  const observer = new MutationObserver(() => {
    theme.value = document.documentElement.getAttribute("data-theme") || "light";
  });
  
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
  
  onMounted(async () => {
    try {
      const user = await authStateReady();

      if (!user) {
        router.replace("/");
        return;
      }

      currentUserId.value = user.uid;
      isLoadingDialogs.value = true;
  
      await loadDialogs();
  
      if (selectedCategory.value === "Сообщения") {
        startListeningForAllNewMessages();
      }
  
      console.log("📦 MDialogs query:", route.query);
    } catch (error) {
      console.error("❌ Ошибка загрузки мобильных диалогов:", error);
    } finally {
      isLoadingDialogs.value = false;
    }
  });
  
  onBeforeUnmount(() => {
    if (unsubscribeAll) {
      unsubscribeAll();
      unsubscribeAll = null;
    }
  
    observer.disconnect();
  });
  
  watch(selectedCategory, async (val) => {
    if (val === "Запросы") {
      if (unsubscribeAll) {
        unsubscribeAll();
        unsubscribeAll = null;
      }
  
      await loadRequests();
    }
  
    if (val === "Сообщения") {
      startListeningForAllNewMessages();
    }
  });
  
  async function loadDialogs() {
  const fetchedDialogs = await ChatsService.getMyChats(currentUserId.value);

  const list = Array.isArray(fetchedDialogs)
    ? fetchedDialogs
    : fetchedDialogs?.chats || [];

  dialogs.value = list
    .map(chat => ({
      id: chat.chatId || chat.chat_id,

      chatType: chat.chatType || chat.chat_type || "default",
      source: chat.source || null,

      interlocutor: {
        userId:
          chat.interlocutor?.userId ||
          chat.interlocutor?.user_id,

        name:
          chat.interlocutor?.name ||
          "Без имени",

        specifics:
          chat.interlocutor?.specifics ||
          "Специальность не указана",

        avatar:
          chat.interlocutor?.avatar ||
          chat.interlocutor?.imageURL ||
          "/default-avatar.png",

        isOnline:
          chat.interlocutor?.isOnline ??
          chat.interlocutor?.is_online ??
          false,
      },

      lastMessage: chat.lastMessage || chat.last_message || null,

      lastMessageText:
        chat.previewText ||
        makeLastMessagePreview(chat.lastMessage || chat.last_message),

      lastMessageDate:
        chat.lastMessage?.sendDate ||
        chat.last_message?.send_date ||
        null,
    }))
    .sort(
      (a, b) =>
        new Date(b.lastMessageDate || 0) -
        new Date(a.lastMessageDate || 0)
    );

  totalUnreadCount.value =
    fetchedDialogs?.totalUnreadMessagesCount ||
    fetchedDialogs?.total_unread_messages_count ||
    0;
}

function makeLastMessagePreview(message) {
  if (!message) return "";

  const text =
    message.textMessage ||
    message.text ||
    message.text_message ||
    "";

  if (text.trim()) return text.trim();

  const imageUrl =
    message.imageUrl ||
    message.image_url;

  if (imageUrl) return "📷 Фото";

  const attachmentName =
    message.attachmentName ||
    message.attachment_name;

  if (attachmentName) return `📎 ${attachmentName}`;

  const attachmentUrl =
    message.attachmentUrl ||
    message.attachment_url;

  if (attachmentUrl) return "📎 Файл";

  return "";
}
  
  async function loadRequests() {
    try {
      const fetched = await RequestsService.getMyRequests(currentUserId.value);
      chatRequests.value = fetched || [];
      console.log("📥 MDialogs запросы получены:", chatRequests.value);
    } catch (error) {
      console.error("❌ Ошибка загрузки запросов:", error);
    }
  }
  
  function startListeningForAllNewMessages() {
  const chatIds = dialogs.value
    .map(dialog => dialog.id)
    .filter(Boolean);

  if (chatIds.length === 0) return;

  if (unsubscribeAll) {
    unsubscribeAll();
    unsubscribeAll = null;
  }

  unsubscribeAll = ChatsService.listenForAllNewMessages(
    chatIds,
    (newMessage) => {
      console.log("📥 MDialogs новое сообщение:", newMessage);
      playNewMessageSound(newMessage);
      const chatId = newMessage.chatId || newMessage.chat_id;

      const chatIndex = dialogs.value.findIndex(
        chat => chat.id === chatId
      );

      if (chatIndex === -1) return;

      const dialog = dialogs.value[chatIndex];
      const previewText = makeLastMessagePreview(newMessage);

      dialogs.value[chatIndex] = {
        ...dialog,
        lastMessageText: previewText,
        lastMessageDate:
          newMessage.sendDate ||
          newMessage.send_date ||
          new Date().toISOString(),
      };

      dialogs.value.sort(
        (a, b) =>
          new Date(b.lastMessageDate || 0) -
          new Date(a.lastMessageDate || 0)
      );

      dialogs.value = [...dialogs.value];
    }
  );
}

function playNewMessageSound(newMessage) {
  const senderId = newMessage.senderId || newMessage.sender_id;

  // свой звук не проигрываем
  if (senderId === currentUserId.value) return;

  const audio = new Audio("/sounds/new-message.mp3");
  audio.volume = 0.45;

  audio.play().catch((error) => {
    console.warn("🔇 Звук не воспроизведён:", error);
  });
}
  
  function toggleDropdown() {
    dropdownVisible.value = !dropdownVisible.value;
  }
  
  function selectCategory(option) {
    selectedCategory.value = option;
    dropdownVisible.value = false;
  }
  
  function handleSelectDialog(dialogId) {
    const dialog = dialogs.value.find(item => item.id === dialogId);
    if (!dialog) return;
    router.push({
    path: "/m-chat",
    query: {
        chat_id: dialog.id,
        user_id: dialog.interlocutor.userId,
        name: dialog.interlocutor.name,
        avatar: dialog.interlocutor.avatar,
        specifics: dialog.interlocutor.specifics,
        source: dialog.source || "default",
    },
    });
}
  
  function handleSelectRequest(requestId) {
    console.log("🔥 MDialogs выбран запрос:", requestId);
  
    router.push({
      path: "/dialogs",
      query: {
        request_id: requestId,
      },
    });
  }
  
  async function handleRequestAccept(id) {
    try {
      const request = chatRequests.value.find(r => r.id === id);
      if (!request) return;
  
      await RequestsService.acceptRequest(
        currentUserId,
        request.user.user_id,
        request.comment,
        request.id
      );
  
      await RequestsService.deleteRequest(id);
  
      chatRequests.value = chatRequests.value.filter(r => r.id !== id);
  
      await loadDialogs();
    } catch (error) {
      console.error("❌ Ошибка при принятии запроса:", error);
    }
  }
  
  async function handleRequestDecline(id) {
    try {
      await RequestsService.deleteRequest(id);
      chatRequests.value = chatRequests.value.filter(r => r.id !== id);
    } catch (error) {
      console.error("❌ Ошибка при отклонении запроса:", error);
    }
  }
  
  function openCreateChatModal() {
    isFriendsDrawerOpen.value = true;
  }
  
  function handleOpenProfile(friend) {
    console.log("👤 MDialogs selected friend:", friend);
  
    if (!friend) return;
  
    router.push({
      path: "/dialogs",
      query: {
        ghost_user_id: friend.user_id || friend.userId,
        name: friend.name,
        image: friend.imageURL || friend.avatar,
        specifics: friend.specifics,
        dialogSource: "friends",
      },
    });
  }
  </script>
  
  <style scoped>

.m-dialogs-page {
  width: 100vw;
  height: 100dvh;
  min-height: 100dvh;

  background-color: var(--sidebar-background);

  display: flex;
  flex-direction: column;

  overflow: hidden;
}
  
  .m-dialogs-content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background-color: var(--sidebar-background);
  }
  
  .top-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 16px 10px;
    position: relative;
    flex-shrink: 0;
  }
  
  .menu-button {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    min-width: 0;
  }
  
  .menu-label {
    font-family: "Eastman-Medium", sans-serif;
    font-size: 18px;
    color: var(--text-primary);
    white-space: nowrap;
  }
  
  .dropdown-icon {
    width: 10px;
    height: 10px;
    margin-top: 2px;
    flex-shrink: 0;
  }
  
  .badge {
    background-color: var(--badge-bg);
    color: var(--badge-text);
    font-size: 11px;
    padding: 4px 8px;
    border-radius: 12px;
    font-family: "Eastman-Regular", sans-serif;
    white-space: nowrap;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .add-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    flex-shrink: 0;
  }
  
  .add-button img {
    width: 28px;
    height: 28px;
  }
  
  .dropdown-menu {
    position: absolute;
    top: 50px;
    left: 16px;
    background: var(--dropdown-bg);
    border: 1px solid var(--dropdown-border);
    border-radius: 10px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
    z-index: 20;
    width: 170px;
    overflow: hidden;
  }
  
  .dropdown-item {
    padding: 12px 14px;
    font-size: 14px;
    font-family: "Eastman-Regular", sans-serif;
    color: var(--text-primary);
    cursor: pointer;
  }
  
  .dropdown-item:hover {
    background-color: var(--dropdown-item-hover);
  }
  
  .dropdown-item.active {
    font-family: "Eastman-Medium", sans-serif;
  }
  
  .search-bar {
    padding: 12px 16px 10px;
    flex-shrink: 0;
  }
  
  [data-theme="dark"] .search-bar {
    background-color: #161616;
    border: 0;
  }
  
  .search-bar input {
    width: 100%;
    box-sizing: border-box;
    padding: 12px 14px;
    border-radius: 14px;
    border: none;
    background-color: var(--input-background);
    outline: none;
    font-family: "Eastman-Regular", sans-serif;
    font-size: 14px;
    color: var(--text-primary);
  }
  
.dialogs-list {
  flex: 1;
  min-height: 0;

  overflow-y: auto;
  overflow-x: hidden;

  padding: 8px 8px 16px;

  display: flex;
  flex-direction: column;

  -webkit-overflow-scrolling: touch;
}
  
  .dialogs-list .empty-state {
    margin: auto;
    text-align: center;
    max-width: 320px;
    width: 100%;
  }
  
  .dialog-skeleton {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 8px;
  }
  
  .avatar-skeleton {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: var(--skeleton-bg);
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
  }
  
  .text-group {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 7px;
    min-width: 0;
  }
  
  .text-line {
    height: 10px;
    border-radius: 6px;
    background: var(--skeleton-bg);
    position: relative;
    overflow: hidden;
  }
  
  .text-line.short {
    width: 42%;
  }
  
  .text-line.long {
    width: 82%;
  }
  
  .shimmer::before,
  .avatar-skeleton::before,
  .text-line::before {
    content: "";
    position: absolute;
    top: 0;
    left: -150px;
    height: 100%;
    width: 150px;
    background: linear-gradient(
      to right,
      transparent 0%,
      var(--skeleton-shimmer) 50%,
      transparent 100%
    );
    animation: shimmer 1.2s infinite;
  }
  
  @keyframes shimmer {
    100% {
      transform: translateX(430px);
    }
  }
  
  @media (min-width: 768px) {
    .m-dialogs-page {
      max-width: 430px;
      margin: 0 auto;
      border-left: 1px solid var(--separator_home);
      border-right: 1px solid var(--separator_home);
    }
  }
  </style>